import * as React from 'react';
import styled from 'styled-components';
import { generateId } from '@agilepoint/utils-common';
import { excludeKeys } from '../../utils';
import { IRadioButtonProps } from './radio-button-props';
import { RadioButtonLabel } from './radio-button-label';

const NativeRadioButton = styled.input.attrs(() => ({
    type: 'radio'
}))`
    position: absolute;
    opacity: 0;
    z-index: -1;
    &:checked + label {
        &:before {
            background-color: ${({ theme: { radioButton: { colors } } }) => colors.checked.checkMarkColor};
            border-color: ${({ theme: { radioButton: { colors } } }) => colors.checked.borderColor};
            box-shadow: 0 0 0 3.5px ${({ theme: { radioButton: { colors } } }) => colors.checked.backgroundColor} inset;
        }
    }
    &:disabled:checked + label::before {
        opacity: 0.5;
        background-color: ${({ theme: { radioButton: { colors } } }) => colors.disabledAndChecked.checkMarkColor};
        border-color: ${({ theme: { radioButton: { colors } } }) => colors.disabledAndChecked.borderColor};
        box-shadow: 0 0 0 3.5px ${({ theme: { radioButton: { colors } } }) => colors.disabledAndChecked.backgroundColor} inset;
    };
    &:disabled + label::before {
        opacity: 0.5;
        background-color: ${({ theme: { radioButton: { colors } } }) => colors.disabled.checkMarkColor};
        border-color: ${({ theme: { radioButton: { colors } } }) => colors.disabled.borderColor};
        box-shadow: 0 0 0 3.5px ${({ theme: { radioButton: { colors } } }) => colors.disabled.backgroundColor} inset;
    }
    &:focus + label::before {
        box-shadow: inset 0 0 0 3.5px ${({ theme: { semantics: { palette } } }) => palette.white}, 0 0 4px ${({ theme: { semantics: { palette } } }) => palette.themePrimary};
    }
`;

const RadioButtonBase = React.memo((props: IRadioButtonProps) => {
    const { label, elementRef, disabled, className, required, hidden, id, onValueChange, onChange, value, title } = props;
    const componentId = React.useMemo(() => id || generateId(), [id]);
    const radioButtonProps = React.useMemo(() => excludeKeys(props, 'onChange', 'elementRef', 'itemRef', 'onValueChange', 'className'), [props]);
    const _onChange = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
        if (onChange !== undefined) {
            onChange(e);
        }

        if (onValueChange !== undefined) {
            onValueChange(value);
        }
    }, [onValueChange, onChange, value]);

    return hidden ? null : (
        <div className={className} ref={elementRef}>
            <NativeRadioButton {...radioButtonProps} id={componentId} onChange={_onChange} />
            <RadioButtonLabel required={required} htmlFor={componentId} disabled={disabled} title={title}>
                {label}
            </RadioButtonLabel>
        </div>
    );
});

RadioButtonBase.displayName = 'RadioButton';

export const RadioButton = styled(RadioButtonBase).attrs({
    className: String('ap-radioButton-wrapper')
})`
    display: inline-block;
    width: auto;
    height: auto;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;
